import { i18n } from "../i18n/i18n";

import React, { Component } from 'react'
import { isEmpty } from 'lodash'
import itemService from '../services/item.service'
import transferService from '../services/transfer.service'
import downloadService from '../services/download.service'
import { TransferData } from '../apis/transfer.api'
import itemApi, {
  ItemData,
  ItemExportAliveTransferItemsRequest,
  ItemListAliveTransfersRequest,
  ItemListAliveTransfersRequestStatus
} from '../apis/item.api'
import Card from '../components/Card'
import PageHeading from '../components/PageHeading'
import Button from '../components/Button'
import TableHorizontal, {
  TableHorizontalHeader
} from '../components/TableHorizontal'
import SearchPicker, { SearchPickerOption } from '../components/SearchPicker'
import Select from '../components/Select'
import Row from '../components/Row/Row'
import Checkbox from '../components/Checkbox'

interface Props {
  onItemSelect: (item: ItemData | null) => void
}

interface State {
  term: string
  itemId: number | null
  status: string
  allItems: boolean
  items: ItemData[]
  aliveTransfers: TransferData[]
}

export default class StockOnHandForm extends Component<Props, State> {
  tableHeaders: TableHorizontalHeader[] = [
    { heading: i18n("Item"), path: 'item' },
    { heading: i18n("Description"), path: 'description' },
    { heading: i18n("Location"), path: 'location' },
    { heading: i18n("Bin"), path: 'bin' },
    { heading: i18n("Units"), path: 'units' },
    { heading: i18n("Value"), path: 'value' },
    { heading: i18n("Safety stock"), path: 'minimumStock' },
    { heading: i18n("Delta"), path: 'delta', delta: true }
  ]

  constructor (props: Props) {
    super(props)
    this.state = {
      term: '',
      itemId: null,
      status: 'inventory',
      allItems: false,
      items: [],
      aliveTransfers: []
    }
    this.handleInputChange = this.handleInputChange.bind(this)
    this.handleCheckChange = this.handleCheckChange.bind(this)
    this.handleTermChange = this.handleTermChange.bind(this)
    this.handleItemSearch = this.handleItemSearch.bind(this)
    this.handleItemSelect = this.handleItemSelect.bind(this)
    this.handleCheckClick = this.handleCheckClick.bind(this)
    this.handleExportClick = this.handleExportClick.bind(this)
    this.handleItemBlur = this.handleItemBlur.bind(this)
  }

  get itemOptions () {
    return itemService.searchOptions(this.state.items)
  }

  get statusSelectOptions () {
    return [
      { name: 'Inventory', value: 'inventory' },
      { name: 'Material', value: 'material' }
    ]
  }

  get stockOnHand () {
    return transferService.stockOnHandData(this.state.aliveTransfers)
  }

  get export () {
    const { aliveTransfers } = this.state
    return !isEmpty(aliveTransfers) &&
      <div className="flex justify-end">
        <Button
          text={i18n("Export")}
          onClick={this.handleExportClick} />
      </div>
  }

  handleInputChange (name: string, value: string) {
    if (name === 'status') {
      this.setState({ status: value })
    }
  }

  handleCheckChange (name: string) {
    if (name === 'allItems') {
      this.setState(state => {
        return { allItems: !state.allItems }
      })
    }
  }

  handleItemSearch (name: string, term: string) {
    itemApi.search(term, items => {
      if (!items) return
      this.setState({ items })
    })
  }

  handleItemBlur () {
    this.setState({ items: [] })
  }

  handleTermChange (name: string, term: string) {
    this.setState({
      itemId: null,
      term,
      aliveTransfers: []
    })
    this.props.onItemSelect(null)
  }

  handleItemSelect (name: string, option: SearchPickerOption) {
    const { value } = option
    const itemId = option.id as number
    const item = itemService.find(this.state.items, itemId)
    if (!item) return
    this.props.onItemSelect(item)
    this.setState({ items: [], term: value, itemId })
  }

  listAliveTransfers () {
    const { itemId, status, allItems } = this.state
    const payload: ItemListAliveTransfersRequest = {}
    if (itemId) payload.item = itemId
    if (status) payload.status = status as ItemListAliveTransfersRequestStatus
    payload.allItems = allItems
    itemApi.listAliveTransfers(payload, aliveTransfers => {
      if (!aliveTransfers) return
      this.setState({ aliveTransfers })
    })
  }

  handleCheckClick () {
    this.listAliveTransfers()
  }

  handleExportClick () {
    const { itemId, status, allItems } = this.state
    const payload: ItemExportAliveTransferItemsRequest = {}
    if (itemId) payload.item = itemId
    if (status) payload.status = status as ItemListAliveTransfersRequestStatus
    payload.allItems = allItems
    itemApi.exportAliveTransferItems(payload, aliveTransfers => {
      if (!aliveTransfers) return
      downloadService.download(aliveTransfers, 'stockOnHand.csv')
    })
  }

  get units () {
    const units = transferService.totalUnits(this.state.aliveTransfers)
    return `${units} units in stock`
  }

  render () {
    return (
      <Card>
        <PageHeading
          text={i18n("Stock On Hand")}
          doc='items-check-items-stock-on-hand' />
        <Row column={2}>
          <div>
            <Select
              name="status"
              value={this.state.status}
              options={this.statusSelectOptions}
              onChange={this.handleInputChange} />
            <Checkbox
              name="allItems"
              label="All items"
              checked={this.state.allItems}
              align="end"
              onChange={this.handleCheckChange} />
            <SearchPicker
              name="term"
              value={this.state.term}
              options={this.itemOptions}
              readonly={this.state.allItems}
              label={i18n("Scan item, enter item code or description")}
              onSearch={this.handleItemSearch}
              onChange={this.handleTermChange}
              onSelect={this.handleItemSelect}
              onBlur={this.handleItemBlur} />
            <div className="flex justify-end">
              <Button
                text={i18n("Check")}
                onClick={this.handleCheckClick} />
            </div>
          </div>
        </Row>
        {this.export}
        <div className="mt-4">
          <p className="mb-1">{this.units}</p>
          <TableHorizontal
            headers={this.tableHeaders}
            data={this.stockOnHand} />
        </div>
      </Card>
    )
  }
}
